import { getApiUrl } from './runtime'
import { readSportsMetadata } from './sportsMetadata'
import type {
  CardCandidate,
  CollectionItem,
  SportsPriceComparable,
} from './types'

interface CardSightPricingRecord {
  title?: string | null
  price: number
  date?: string | null
  source: string
  listing_type?: 'auction' | 'fixed' | null
  url?: string | null
  parallel_id?: string | null
  parallel_name?: string | null
}

interface CardSightPricingResponse {
  raw?: { records?: CardSightPricingRecord[] }
  graded?: Array<{
    company_name: string
    company_id: string
    grades?: Array<{
      grade_value: string
      grade_id: string
      records?: CardSightPricingRecord[]
    }>
  }>
}

interface CardSightBulkPricingResponse {
  results?: Array<{
    card_id: string
    success: boolean
    data?: CardSightPricingResponse
  }>
  error?: string
}

export type SportsPricingMode = 'intro' | 'batch' | 'manual'

export interface SportsPricingStatus {
  configured: boolean
  identificationEnabled?: boolean
  automaticPricing?: boolean
  scanMode?: 'front-local-ocr'
  pricingMode: 'manual-batch'
  batchTarget: number
  introLimit: number
  introRequestsUsed: number
  introRemaining: number
  manualPartialAllowed: boolean
}

export type SportsPriceUpdate = Pick<
  CollectionItem,
  | 'priceUsd'
  | 'priceLabel'
  | 'updatedAt'
  | 'lastPriceCheck'
  | 'sportsPriceStatus'
  | 'sportsPriceComparables'
  | 'sportsPriceExpiresAt'
>

const sportsSegments = [
  'baseball',
  'basketball',
  'boxing',
  'football',
  'golf',
  'hockey',
  'mma',
  'racing',
  'soccer',
  'tennis',
  'wrestling',
]

export async function searchSportsCards(
  query: string,
  options: { limit?: number; offset?: number; authToken?: string } = {},
): Promise<CardCandidate[]> {
  const cleanQuery = query.trim().replace(/\s+/g, ' ')
  if (cleanQuery.length < 2) {
    return []
  }
  const parameters = new URLSearchParams({ q: cleanQuery, limit: String(options.limit ?? 12), offset: String(options.offset ?? 0) })
  const payload = await requestJson<{ items: Array<{ id: string; title: string; url: string; imageUrl: string }> }>(
    getApiUrl(`/api/sports/search?${parameters}`), options.authToken,
  )
  return payload.items.map(item => {
    const metadata = readSportsMetadata(item.title)
    const sport = inferSportFromText(item.title)
    return {
      sourceId: `ebay-${item.id}`,
      game: 'sports',
      name: item.title,
      setName: 'Details needed',
      setCode: '',
      collectorNumber: '',
      rarity: 'Unconfirmed',
      colors: sport === 'Sports' ? [] : [sport],
      category: sport,
      imageUrl: item.imageUrl,
      priceUsd: null,
      priceLabel: 'Value unavailable',
      sourceUrl: item.url,
      updatedAt: new Date().toISOString(),
      sport,
      sportsPriceStatus: 'unavailable',
      ...metadata,
    }
  })
}

export function buildSportsReferenceUrl(query: string): string {
  const cleanQuery = query.trim().replace(/\s+/g, ' ')
  if (!cleanQuery) {
    return ''
  }
  const url = new URL('https://www.ebay.com/sch/i.html')
  url.searchParams.set('_nkw', cleanQuery)
  return url.toString()
}

export async function fetchSportsPriceUpdates(
  items: CollectionItem[],
  authToken: string,
  mode: SportsPricingMode = 'batch',
): Promise<Map<string, SportsPriceUpdate>> {
  const sourceIds = Array.from(
    new Set(
      items
        .filter((item) => item.game === 'sports' && isCardSightId(item.sourceId))
        .map((item) => item.sourceId),
    ),
  ).slice(0, 100)
  if (!sourceIds.length) {
    return new Map()
  }

  const payload = await requestJson<CardSightBulkPricingResponse>(
    getApiUrl('/api/cardsight/pricing/bulk'),
    authToken,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cardIds: sourceIds, mode }),
    },
  )
  const providerResults = new Map(
    (payload.results ?? []).map((result) => [result.card_id, result]),
  )
  const updates = new Map<string, SportsPriceUpdate>()
  const now = new Date()

  for (const item of items) {
    if (item.game !== 'sports' || updates.has(item.sourceId)) {
      continue
    }
    const providerResult = providerResults.get(item.sourceId)
    updates.set(
      item.sourceId,
      buildPriceUpdate(item, providerResult?.success ? providerResult.data : undefined, now),
    )
  }
  return updates
}

export async function getSportsPricingStatus(
  authToken: string,
): Promise<SportsPricingStatus> {
  return requestJson<SportsPricingStatus>(
    getApiUrl('/api/cardsight/status'),
    authToken,
  )
}

export function buildPriceUpdate(
  item: CollectionItem,
  pricing: CardSightPricingResponse | undefined,
  now = new Date(),
): SportsPriceUpdate {
  const comparableRecords = selectPricingRecords(item, pricing)
    .filter(
      (record) =>
        record.listing_type !== 'fixed' &&
        Number.isFinite(record.price) &&
        record.price > 0,
    )
    .sort((left, right) => dateValue(right.date) - dateValue(left.date))
  const seen = new Set<string>()
  const comparables: SportsPriceComparable[] = []

  for (const record of comparableRecords) {
    const key = record.url || `${record.title ?? ''}|${record.price}|${record.date ?? ''}`
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    comparables.push({
      title: record.title?.trim() || `${record.source} completed sale`,
      price: record.price,
      date: record.date ?? '',
      source: record.source,
      url: record.url ?? '',
    })
    if (comparables.length === 3) {
      break
    }
  }

  const checkedAt = now.toISOString()
  const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString()
  if (!comparables.length) {
    return {
      priceUsd: null,
      priceLabel: 'No sold comps',
      updatedAt: checkedAt,
      lastPriceCheck: checkedAt,
      sportsPriceStatus: 'unavailable',
      sportsPriceComparables: [],
      sportsPriceExpiresAt: expiresAt,
    }
  }

  const average = comparables.reduce((total, comp) => total + comp.price, 0) / comparables.length
  return {
    priceUsd: average,
    priceLabel: `Sold avg $${average.toFixed(2)}`,
    updatedAt: checkedAt,
    lastPriceCheck: checkedAt,
    sportsPriceStatus: 'priced',
    sportsPriceComparables: comparables,
    sportsPriceExpiresAt: expiresAt,
  }
}

function selectPricingRecords(
  item: CollectionItem,
  pricing: CardSightPricingResponse | undefined,
): CardSightPricingRecord[] {
  let records: CardSightPricingRecord[] = []
  if (item.gradingCompany || item.grade || item.gradeId) {
    const company = (pricing?.graded ?? []).find(
      (entry) =>
        (item.gradingCompanyId && entry.company_id === item.gradingCompanyId) ||
        sameText(entry.company_name, item.gradingCompany),
    )
    const grade = company?.grades?.find(
      (entry) =>
        (item.gradeId && entry.grade_id === item.gradeId) ||
        sameText(entry.grade_value, item.grade),
    )
    records = grade?.records ?? []
  } else {
    records = pricing?.raw?.records ?? []
  }

  if (item.parallelId) {
    return records.filter((record) => record.parallel_id === item.parallelId)
  }
  if (item.parallelName) {
    return records.filter((record) => sameText(record.parallel_name, item.parallelName))
  }
  return records.filter((record) => !record.parallel_id && !record.parallel_name)
}

async function requestJson<T>(
  input: RequestInfo | URL,
  authToken = '',
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers)
  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`)
  }
  const response = await fetch(input, { ...init, headers })
  const payload = (await response.json()) as T & { error?: string }
  if (!response.ok || payload.error) {
    throw new Error(payload.error || 'Sports-card provider request failed.')
  }
  return payload
}

function inferSportFromText(value: string): string {
  const normalized = value.trim().toLowerCase()
  const found = sportsSegments.find((sport) => normalized.includes(sport))
  return found ? found.replace(/^./, (letter) => letter.toUpperCase()) : 'Sports'
}

export function isCardSightId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  )
}

function sameText(left: string | null | undefined, right: string | undefined): boolean {
  return Boolean(left && right && left.trim().toLowerCase() === right.trim().toLowerCase())
}

function dateValue(value: string | null | undefined): number {
  const parsed = value ? new Date(value).getTime() : 0
  return Number.isFinite(parsed) ? parsed : 0
}
